import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Loader2, MailCheck } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-ink-50 px-6 py-16">
      <div className="max-w-md w-full bg-white border border-ink-100 p-10" data-testid="forgot-password-page">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src="/logo-128.png" alt="LeadIQ Pro" width="40" height="40" className="w-10 h-10" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-base tracking-tight text-ink-900">
              LeadIQ Pro
            </div>
            <div className="text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
              Account recovery
            </div>
          </div>
        </Link>

        {done ? (
          <div data-testid="forgot-password-success">
            <div className="w-12 h-12 grid place-items-center bg-ink-900 text-white mb-6">
              <MailCheck className="w-6 h-6" />
            </div>
            <h1 className="font-display text-3xl font-extrabold text-ink-900 leading-tight mb-3">
              Check your inbox.
            </h1>
            <p className="text-ink-500 text-sm mb-8 leading-relaxed">
              If <span className="font-mono text-ink-900">{email}</span> matches an account,
              we just sent a reset link. It's good for 30 minutes.
            </p>
            <Link
              to="/login"
              className="text-sm text-brand hover:text-brand-hover font-semibold underline-offset-4 hover:underline"
              data-testid="back-to-login-link"
            >
              ← Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink-900 leading-tight mb-3">
              Forgot password?
            </h1>
            <p className="text-ink-500 text-sm mb-8 leading-relaxed">
              Enter the email you signed up with. We'll send a one-time link to reset your password.
            </p>
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-password-form">
              <div>
                <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
                  Work email
                </Label>
                <Input
                  data-testid="forgot-password-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="mt-2 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11 font-mono text-sm"
                />
              </div>
              <Button
                data-testid="forgot-password-submit-button"
                type="submit"
                disabled={busy || !email}
                className="w-full h-11 rounded-sm bg-brand hover:bg-brand-hover text-white font-medium tracking-wide group"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Send reset link <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>)}
              </Button>
            </form>
            <div className="mt-8 text-sm text-ink-500">
              Remembered it?{" "}
              <Link to="/login" className="text-brand hover:text-brand-hover font-medium underline-offset-4 hover:underline" data-testid="forgot-back-to-login">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
